import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PincodeInput from "@/components/common/PincodeInput";
import FormError from "@/components/common/FormError";
import { toast } from "sonner";
import {
  ArrowLeft, Building2, Landmark, User, Loader2,
} from "lucide-react";

const SIGNUP_ROLES = ["bank", "field"];

const ROLE_META = {
  bank: { icon: Landmark, color: "bg-emerald-600" },
  field: { icon: User, color: "bg-amber-600" },
};

const emptyForm = {
  email: "", password: "", name: "", phone: "",
  bank_name: "", branch: "", registered_person: "", bank_location: "", portal_link: "", address: "",
  home_address: "", working_location: "", working_radius_km: 25, kyc_aadhar: "", kyc_pan: "",
  designation: "",
  city: "", pincode: "",
};

export default function Signup() {
  const { role } = useParams();
  const navigate = useNavigate();
  const { completeSignup } = useAuth();
  const { t } = useLang();

  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pincodeOk, setPincodeOk] = useState(false);

  useEffect(() => {}, []);

  if (!SIGNUP_ROLES.includes(role)) {
    return <Navigate to={`/login/${role || "bank"}`} replace />;
  }
  const meta = ROLE_META[role];
  const Icon = meta.icon;

  const upd = (k) => (e) => { setError(""); setForm({ ...form, [k]: e?.target ? e.target.value : e }); };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!pincodeOk) {
      setError("Please enter a valid Rajasthan pincode");
      return;
    }
    setBusy(true);
    setError("");
    try {
      // for signup, pass district as bank_location or working_location depending on role
      const payload = { ...form, role, email: form.email.trim().toLowerCase() };
      if (role === "bank") payload.bank_location = form.bank_location || form.city;
      if (role === "field") payload.working_location = form.working_location;
      const { data } = await api.post("/auth/signup", payload);
      completeSignup(data);
      toast.success("Account created — welcome!");
      navigate(`/${data.user.role}`);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 grid lg:grid-cols-5">
      {/* Left banner (hidden on mobile) */}
      <div className="hidden lg:block lg:col-span-2 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1600531529272-023c4b821f14?crop=entropy&cs=srgb&fm=jpg&q=85)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/90 via-slate-950/75 to-slate-900/60" />
        <div className="relative z-10 p-12 h-full flex flex-col justify-between text-white">
          <Link to="/" className="flex items-center gap-2 w-fit" data-testid="signup-brand-link">
            <div className="h-9 w-9 rounded-md bg-white text-slate-950 flex items-center justify-center">
              <Building2 className="h-5 w-5" />
            </div>
            <div className="font-heading font-bold">Rana Associates</div>
          </Link>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300 mb-3">
              {t(role)} · SIGN UP
            </p>
            <h1 className="font-heading text-4xl font-bold tracking-tight leading-tight max-w-md">
              Join the platform
            </h1>
            <p className="mt-4 text-slate-300 max-w-md">
              Create your account and start operating within minutes.
            </p>
          </div>
          <div className="text-xs text-slate-400">
            © {new Date().getFullYear()} Rana Associates
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="lg:col-span-3 flex items-start justify-center p-6 sm:p-10">
        <div className="w-full max-w-2xl">
          <Link
            to={`/login/${role}`}
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-8"
            data-testid="signup-back-login"
          >
            <ArrowLeft className="h-4 w-4" /> Back to login
          </Link>

          <div className={`h-11 w-11 rounded-md ${meta.color} text-white flex items-center justify-center mb-6`}>
            <Icon className="h-5 w-5" />
          </div>

          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
            {String(role).toUpperCase()} SIGN UP
          </p>
          <h2 className="mt-2 font-heading text-3xl font-bold text-slate-950">Create your {t(role)} account</h2>
          <p className="mt-2 text-slate-600 text-sm">
            Fill in the details below. You'll be signed in automatically.
          </p>

          <form onSubmit={submit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="signup-form">
            {/* Common fields */}
            <div>
              <Label>Full name</Label>
              <Input required value={form.name} onChange={upd("name")} data-testid="signup-name" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input required value={form.phone} onChange={upd("phone")} data-testid="signup-phone" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" required value={form.email} onChange={upd("email")} onBlur={() => setForm((f) => ({ ...f, email: f.email.trim().toLowerCase() }))} autoComplete="email" data-testid="signup-email" />
            </div>
            <div>
              <Label>Password</Label>
              <Input type="password" required minLength={8} pattern="(?=.*[A-Za-z])(?=.*\d).{8,}" title="At least 8 characters with a letter and a number" value={form.password} onChange={upd("password")} autoComplete="new-password" data-testid="signup-password" placeholder="min 8 chars, letters + numbers" />
            </div>

            {/* Role-specific fields */}
            {role === "bank" && (
              <>
                <div>
                  <Label>Bank / NBFC name</Label>
                  <Input required value={form.bank_name} onChange={upd("bank_name")} data-testid="signup-bank-name" />
                </div>
                <div>
                  <Label>Branch</Label>
                  <Input required value={form.branch} onChange={upd("branch")} data-testid="signup-bank-branch" />
                </div>
                <div>
                  <Label>Registered Person</Label>
                  <Input value={form.registered_person} onChange={upd("registered_person")} data-testid="signup-bank-person" />
                </div>
                <PincodeInput
                  value={form.pincode}
                  onChange={(p) => { setForm({ ...form, pincode: p }); setPincodeOk(false); }}
                  onResolved={(r) => {
                    setPincodeOk(r.isRajasthan);
                    if (r.isRajasthan) setForm({ ...form, pincode: r.pincode, city: r.city, bank_location: r.district });
                  }}
                  testId="signup-bank-pincode"
                  label="Pincode"
                  required
                />
                <div>
                  <Label>City</Label>
                  <Input value={form.city} readOnly className="bg-slate-50" data-testid="signup-bank-city" />
                </div>
                <div>
                  <Label>District</Label>
                  <Input value={form.bank_location} readOnly className="bg-slate-50" data-testid="signup-bank-district" />
                </div>
                <div className="md:col-span-2">
                  <Label>Address</Label>
                  <Textarea value={form.address} onChange={upd("address")} data-testid="signup-bank-address" />
                </div>
                <div className="md:col-span-2">
                  <Label>Valuation portal link (optional)</Label>
                  <Input value={form.portal_link} onChange={upd("portal_link")} placeholder="https://valuation.bank.co.in" data-testid="signup-bank-portal" />
                  <p className="text-xs text-slate-500 mt-1">Your account gets full portal access after Admin approval.</p>
                </div>
              </>
            )}

            {role === "field" && (
              <>
                <PincodeInput
                  value={form.pincode}
                  onChange={(p) => { setForm({ ...form, pincode: p }); setPincodeOk(false); }}
                  onResolved={(r) => {
                    setPincodeOk(r.isRajasthan);
                    if (r.isRajasthan) setForm({ ...form, pincode: r.pincode, city: r.city, working_location: r.district });
                  }}
                  testId="signup-field-pincode"
                  label="Working Pincode"
                  required
                />
                <div>
                  <Label>City</Label>
                  <Input value={form.city} readOnly className="bg-slate-50" data-testid="signup-field-city" />
                </div>
                <div>
                  <Label>Working District</Label>
                  <Input value={form.working_location} readOnly className="bg-slate-50" data-testid="signup-field-district" />
                </div>
                <div>
                  <Label>Working radius (km)</Label>
                  <Input type="number" value={form.working_radius_km} onChange={upd("working_radius_km")} data-testid="signup-field-radius" />
                </div>
                <div className="md:col-span-2">
                  <Label>Home address</Label>
                  <Textarea required value={form.home_address} onChange={upd("home_address")} data-testid="signup-field-home" />
                </div>
                <div>
                  <Label>Aadhaar number</Label>
                  <Input value={form.kyc_aadhar} onChange={upd("kyc_aadhar")} placeholder="XXXX-XXXX-XXXX" data-testid="signup-field-aadhaar" />
                </div>
                <div>
                  <Label>PAN number</Label>
                  <Input value={form.kyc_pan} onChange={upd("kyc_pan")} placeholder="ABCDE1234F" data-testid="signup-field-pan" />
                </div>
              </>
            )}

            {role === "office" && (
              <div className="md:col-span-2">
                <Label>Designation</Label>
                <Input value={form.designation} onChange={upd("designation")} placeholder="e.g. Operations Coordinator" data-testid="signup-office-designation" />
              </div>
            )}

            <div className="md:col-span-2 mt-2 space-y-3">
              <FormError message={error} testId="signup-error" />
              {error && /already registered/i.test(error) && (
                <Link to={`/login/${role}`} className="block text-sm text-blue-600 hover:underline" data-testid="signup-error-goto-login">
                  Go to sign in →
                </Link>
              )}
              <Button
                type="submit"
                disabled={busy}
                aria-busy={busy}
                className="w-full bg-slate-950 hover:bg-slate-800 text-white h-11"
                data-testid="signup-submit-button"
              >
                {busy ? (<><Loader2 className="h-4 w-4 animate-spin mr-2" /> Creating account…</>) : "Create account"}
              </Button>
            </div>

            <div className="md:col-span-2 text-center text-sm text-slate-600">
              Already have an account?{" "}
              <Link to={`/login/${role}`} className="text-blue-600 hover:underline font-medium" data-testid="signup-goto-login">
                Sign in
              </Link>
            </div>
          </form>

          {/* Role switcher */}
          <div className="mt-8 grid grid-cols-2 gap-2">
            {SIGNUP_ROLES.map((r) => (
              <Link
                key={r}
                to={`/signup/${r}`}
                className={`text-center text-xs py-2 rounded-md border ${
                  r === role
                    ? "bg-slate-950 text-white border-slate-950"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                }`}
                data-testid={`signup-switch-role-${r}`}
              >
                {t(r)}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
